import { useEffect } from 'react';
import { useParams } from 'react-router';
import { useSession } from '@sdk/session/SessionContext';

import { useSocketConnectionContext } from '@web/components/SocketConnectionProvider/SocketConnectionProvider';
import { IncomingCommand } from '@web/components/SocketConnectionProvider/types/command';
import { useConversationMappers } from '@web/conversations/hooks/useConversationMappers';
import { useConversationQueryCache } from '@web/conversations/hooks/useConversationQueryCache';
import { mapNewConversation, mapEditedConversation } from '@web/conversations/utils/mappers';

export const useConversationsSocketListener = () => {
  // @TODO consider using better technique to get possibly active conversation id
  // useMatch(PathEnum.CONVERSATION_DETAILS) seems to be a good choice, however
  // the hook won't work correctly with optional params - :slug in our case.
  const { id } = useParams();

  const { currentUser } = useSession();
  const { subscribe } = useSocketConnectionContext();
  const { mapAPIMessageStrategy } = useConversationMappers();
  const {
    addConversations,
    addMessage,
    refreshConversations,
    removeConversation,
    removeMessage,
    updateConversation,
    updateMessage,
    updateReactions,
    updateUnreadStateByNewMessage,
  } = useConversationQueryCache();

  useEffect(
    () =>
      subscribe(IncomingCommand.CONVERSATION_CREATE, ({ msg: data }) => {
        const mappedConversation = mapNewConversation(data);
        addConversations(mappedConversation);
      }),
    [subscribe, addConversations]
  );

  useEffect(
    () =>
      subscribe(IncomingCommand.CONVERSATION_DELETE, ({ msg: data }) => {
        removeConversation(data.id);
      }),
    [subscribe, removeConversation]
  );

  useEffect(
    () =>
      subscribe(IncomingCommand.CONVERSATION_UPDATE, ({ msg: data }) => {
        const mappedConversation = mapEditedConversation(data);
        updateConversation(mappedConversation.id, mappedConversation);
      }),
    [subscribe, updateConversation]
  );

  useEffect(() => {
    const removeGroupCreateListener = subscribe(IncomingCommand.GROUP_CREATE, () => {
      refreshConversations();
    });

    const removeGroupDeleteListener = subscribe(IncomingCommand.GROUP_DELETE, () => {
      refreshConversations();
    });

    const removeGroupUpdateListener = subscribe(IncomingCommand.GROUP_UPDATE, () => {
      refreshConversations();
    });

    return () => {
      removeGroupCreateListener();
      removeGroupDeleteListener();
      removeGroupUpdateListener();
    };
  }, [refreshConversations, subscribe]);

  useEffect(
    () =>
      subscribe(IncomingCommand.MESSAGE_CREATE, ({ msg: data }) => {
        const mappedMessage = mapAPIMessageStrategy(data);

        addMessage(mappedMessage);

        const eventSentByCurrentUser = currentUser.id === mappedMessage.author.id;
        const conversationId = id ? Number(id) : null;
        const isConversationActive = conversationId === data.conversationId && document.hasFocus();

        if (!eventSentByCurrentUser && !isConversationActive) {
          updateUnreadStateByNewMessage(mappedMessage);
        }
      }),
    [
      addMessage,
      mapAPIMessageStrategy,
      subscribe,
      updateUnreadStateByNewMessage,
      currentUser.id,
      id,
    ]
  );

  useEffect(
    () =>
      subscribe(IncomingCommand.MESSAGE_UPDATE, ({ msg: data }) => {
        if (data.deleted) {
          removeMessage(data.conversationId, data.id);
        } else {
          const mappedMessage = mapAPIMessageStrategy(data);
          updateMessage(mappedMessage.conversationId, mappedMessage);
        }
      }),
    [mapAPIMessageStrategy, removeMessage, updateMessage, subscribe]
  );

  useEffect(
    () =>
      subscribe(IncomingCommand.REACTION_UPDATE, ({ msg: data }) => {
        const reactions = data.emojiReactions;

        updateReactions(data.conversationId, data.messageId, reactions);
      }),
    [subscribe, updateReactions]
  );
};
